'use strict';

/**
 * Wizard offre partenaire — calcul net / aperçus additifs (accessoires, forfait, reprise).
 *
 * Base des remises % et montant : prix catalogue avec options (#offer-custom-price, champ caché).
 */

import {
    parseSwissMoney,
    formatSwissMoney,
    countSignificantCharsBeforeCaret,
    mapCaretAfterFormat,
    formatSwissMoneyLive,
} from './swiss-number-format.mjs';

const DISCOUNT_TYPE_PERCENT = 'pourcentage';
const DISCOUNT_TYPE_AMOUNT = 'montant';
const DISCOUNT_TYPE_FIXED_PRICE = 'prix_fixe';
const DEFAULT_MAX_ACCESSORIES = 20;

/**
 * Parse un montant / une valeur numérique saisie (source de parse partagée).
 *
 * @param {string|number|null|undefined} raw
 * @returns {number|null}
 */
function parseAmount(raw) {
    return parseSwissMoney(raw);
}

/**
 * Indique si le type de remise correspond à un montant CHF (donc formaté milliers).
 *
 * @param {string} discountType
 * @returns {boolean}
 */
function isMonetaryDiscountType(discountType) {
    return discountType === DISCOUNT_TYPE_AMOUNT || discountType === DISCOUNT_TYPE_FIXED_PRICE;
}

/**
 * @param {number} value
 * @returns {number}
 */
function roundMoney(value) {
    return Math.round(value * 100) / 100;
}

/**
 * Calcule le prix net proposé à partir du prix catalogue avec options + type de remise.
 *
 * @param {number|null} catalogWithOptionsPrice
 * @param {string} discountType
 * @param {number|null} discountValue
 * @returns {number|null}
 */
export function computeOfferNetPrice(catalogWithOptionsPrice, discountType, discountValue) {
    if (discountType === DISCOUNT_TYPE_FIXED_PRICE) {
        // Prix fixe sans saisie : pas de preview trompeuse (= catalogue).
        if (discountValue === null) {
            return null;
        }

        return roundMoney(Math.max(0, discountValue));
    }

    if (catalogWithOptionsPrice === null) {
        return null;
    }

    if (discountValue === null) {
        return roundMoney(catalogWithOptionsPrice);
    }

    if (discountType === DISCOUNT_TYPE_PERCENT) {
        return roundMoney(Math.max(0, catalogWithOptionsPrice * (1 - discountValue / 100)));
    }

    if (discountType === DISCOUNT_TYPE_AMOUNT) {
        return roundMoney(Math.max(0, catalogWithOptionsPrice - discountValue));
    }

    return roundMoney(catalogWithOptionsPrice);
}

/**
 * Valeur du champ remise après changement de type (vide si le type a changé).
 *
 * @param {string} previousType
 * @param {string} nextType
 * @param {string} currentValue
 * @returns {string}
 */
export function discountValueAfterTypeChange(previousType, nextType, currentValue) {
    if (previousType !== nextType) {
        return '';
    }

    return currentValue;
}

/**
 * Somme des prix accessoires (lignes partielles ignorées côté preview).
 * Ne comptabilise que les lignes visibles (slots Fluid non masqués).
 *
 * @param {Iterable<HTMLInputElement>} priceInputs
 * @returns {number}
 */
export function sumAccessoryPricesFromInputs(priceInputs) {
    let sum = 0;
    for (const input of priceInputs) {
        if (!(input instanceof HTMLInputElement)) {
            continue;
        }
        const row = input.closest('[data-offer-accessory-row]');
        if (row instanceof HTMLElement && row.hidden) {
            continue;
        }
        const descInput = row instanceof HTMLElement
            ? row.querySelector('input[name*="[description]"]')
            : null;
        const description = descInput instanceof HTMLInputElement ? descInput.value.trim() : '';
        const price = parseAmount(input.value);
        if (description === '' || price === null || price < 0) {
            continue;
        }
        sum += price;
    }

    return roundMoney(sum);
}

/**
 * Totaux additifs : base net null → 0 (pas de fusion dans computeOfferNetPrice).
 *
 * @param {number|null} netPrice
 * @param {number} accessoriesSum
 * @param {number|null} deliveryFee
 * @param {boolean} [hasAccessories=false]
 * @param {number|null} [tradeInAmount=null]
 * @returns {{ vehiclePlus: number, salePrice: number, hasAdditive: boolean }}
 */
export function computeAdditiveTotals(netPrice, accessoriesSum, deliveryFee, hasAccessories = false, tradeInAmount = null) {
    const baseNet = netPrice === null ? 0 : netPrice;
    const fee = deliveryFee === null ? 0 : deliveryFee;
    const tradeIn = (tradeInAmount !== null && tradeInAmount >= 0) ? tradeInAmount : 0;
    const hasTradeIn = tradeInAmount !== null && tradeInAmount >= 0;
    const hasAdditive = hasAccessories || accessoriesSum > 0 || deliveryFee !== null || hasTradeIn;
    const vehiclePlus = roundMoney(baseNet + accessoriesSum);
    const salePrice = roundMoney(Math.max(0, vehiclePlus + fee - tradeIn));

    return { vehiclePlus, salePrice, hasAdditive };
}

/**
 * @param {number} value
 * @returns {string}
 */
function formatSwissGroupedAmount(value) {
    return formatSwissMoney(value, true);
}

/**
 * Formatage live money avec préservation du caret.
 *
 * @param {HTMLInputElement} input
 * @param {InputEvent|Event} [event]
 */
function applyMoneyLiveFormat(input, event) {
    if (input.dataset.swissOfferComposing === '1') {
        return;
    }
    if (event && event.isComposing) {
        return;
    }

    const previous = input.value;
    const caretPos = input.selectionStart ?? previous.length;
    const significantCount = countSignificantCharsBeforeCaret(previous, caretPos);
    const formatted = formatSwissMoneyLive(previous);

    if (formatted === null || formatted === previous) {
        return;
    }

    input.value = formatted;
    const nextCaret = mapCaretAfterFormat(formatted, significantCount);
    try {
        input.setSelectionRange(nextCaret, nextCaret);
    } catch (_error) {
        // Ignore.
    }
}

/**
 * @param {HTMLElement} root
 */
function initPartnerOfferPricing(root) {
    if (root.dataset.offerPricingBound === '1') {
        return;
    }
    root.dataset.offerPricingBound = '1';

    const discountTypeSelect = root.querySelector('#offer-discount-type');
    const discountValueInput = root.querySelector('#offer-discount-value');
    // Snapshot catalogue avec options (caché) — base % / montant.
    const catalogBasePriceInput = root.querySelector('#offer-custom-price');
    const netPreview = root.querySelector('[data-offer-net-preview]');
    const netPriceValue = root.querySelector('[data-offer-net-price-value]');
    const discountValueLabel = root.querySelector('[data-offer-discount-value-label]');
    const deliveryFeeInput = root.querySelector('[data-offer-delivery-fee], #offer-delivery-fee');
    const accessoriesRoot = root.querySelector('[data-offer-accessories-root]');
    const accessoriesList = root.querySelector('[data-offer-accessories-list]');
    const accessoryAddButton = root.querySelector('[data-offer-accessory-add]');
    const additivePreviews = root.querySelector('[data-offer-additive-previews]');
    const salePricePreviewWrap = root.querySelector('[data-offer-sale-price-preview-wrap]');
    const vehiclePlusPreview = root.querySelector('[data-offer-vehicle-plus-preview]');
    const vehiclePlusValue = root.querySelector('[data-offer-vehicle-plus-value]');
    const salePriceValue = root.querySelector('[data-offer-sale-price-value]');
    const tradeInLabelInput = root.querySelector('[data-offer-trade-in-label], #offer-trade-in-label');
    const tradeInAmountInput = root.querySelector('[data-offer-trade-in-amount], #offer-trade-in-amount');
    const tradeInError = root.querySelector('[data-offer-trade-in-error]');

    if (
        !(discountTypeSelect instanceof HTMLSelectElement)
        || !(discountValueInput instanceof HTMLInputElement)
        || !(catalogBasePriceInput instanceof HTMLInputElement)
        || !(netPreview instanceof HTMLElement)
        || !(netPriceValue instanceof HTMLElement)
    ) {
        return;
    }

    const currencySuffix = (root.getAttribute('data-offer-currency-chf') || 'CHF').trim();
    const labelPercent = root.getAttribute('data-offer-label-discount-percent') || 'Remise (%)';
    const labelAmount = root.getAttribute('data-offer-label-discount-amount') || 'Montant de la remise (CHF)';
    const labelFixedPrice = root.getAttribute('data-offer-label-discount-fixed') || 'Prix final (CHF)';
    const maxAccessories = Number.parseInt(
        accessoriesRoot instanceof HTMLElement
            ? (accessoriesRoot.getAttribute('data-offer-accessories-max') || String(DEFAULT_MAX_ACCESSORIES))
            : String(DEFAULT_MAX_ACCESSORIES),
        10,
    ) || DEFAULT_MAX_ACCESSORIES;

    const updateDiscountValueLabel = () => {
        if (!(discountValueLabel instanceof HTMLLabelElement)) {
            return;
        }

        const discountType = discountTypeSelect.value;
        if (discountType === DISCOUNT_TYPE_FIXED_PRICE) {
            discountValueLabel.textContent = labelFixedPrice;
            return;
        }

        if (discountType === DISCOUNT_TYPE_AMOUNT) {
            discountValueLabel.textContent = labelAmount;
            return;
        }

        discountValueLabel.textContent = labelPercent;
    };

    const getAccessoryRows = () => {
        if (!(accessoriesList instanceof HTMLElement)) {
            return [];
        }

        return Array.from(accessoriesList.querySelectorAll('[data-offer-accessory-row]'));
    };

    const getVisibleAccessoryRows = () => getAccessoryRows().filter((row) => !row.hidden);

    const clearAccessoryRow = (row) => {
        row.querySelectorAll('input').forEach((input) => {
            if (input instanceof HTMLInputElement) {
                input.value = '';
            }
        });
    };

    const updateAddButtonState = () => {
        if (!(accessoryAddButton instanceof HTMLButtonElement)) {
            return;
        }
        accessoryAddButton.disabled = getVisibleAccessoryRows().length >= maxAccessories;
    };

    const updateAdditivePreviews = (netPrice) => {
        const visibleRows = getVisibleAccessoryRows();
        const priceInputs = [];
        visibleRows.forEach((row) => {
            const priceInput = row.querySelector('[data-offer-accessory-price]');
            if (priceInput instanceof HTMLInputElement) {
                priceInputs.push(priceInput);
            }
        });
        const accessoriesSum = sumAccessoryPricesFromInputs(priceInputs);
        let hasAccessories = false;
        for (const input of priceInputs) {
            const row = input.closest('[data-offer-accessory-row]');
            const descInput = row instanceof HTMLElement
                ? row.querySelector('input[name*="[description]"]')
                : null;
            const description = descInput instanceof HTMLInputElement ? descInput.value.trim() : '';
            const price = parseAmount(input.value);
            if (description !== '' && price !== null && price >= 0) {
                hasAccessories = true;
                break;
            }
        }

        let deliveryFee = null;
        if (deliveryFeeInput instanceof HTMLInputElement && deliveryFeeInput.value.trim() !== '') {
            deliveryFee = parseAmount(deliveryFeeInput.value);
        }

        // Forfait saisi (même 0) compte comme additif ; champ vide → null.
        const feeForTotals = deliveryFeeInput instanceof HTMLInputElement
            && deliveryFeeInput.value.trim() !== ''
            && deliveryFee !== null
            ? deliveryFee
            : null;

        let tradeInAmount = null;
        const tradeInLabel = tradeInLabelInput instanceof HTMLInputElement
            ? tradeInLabelInput.value.trim()
            : '';
        if (tradeInAmountInput instanceof HTMLInputElement && tradeInAmountInput.value.trim() !== '') {
            tradeInAmount = parseAmount(tradeInAmountInput.value);
        }
        const hasCompleteTradeIn = tradeInLabel !== ''
            && tradeInAmount !== null
            && tradeInAmount >= 0;
        const tradeInForTotals = hasCompleteTradeIn ? tradeInAmount : null;

        const totals = computeAdditiveTotals(
            netPrice,
            accessoriesSum,
            feeForTotals,
            hasAccessories,
            tradeInForTotals,
        );

        const subtotalBeforeTradeIn = roundMoney(
            (netPrice === null ? 0 : netPrice) + accessoriesSum + (feeForTotals === null ? 0 : feeForTotals),
        );
        const tradeInExceeds = hasCompleteTradeIn && tradeInAmount > subtotalBeforeTradeIn + 0.005;
        if (tradeInError instanceof HTMLElement) {
            tradeInError.hidden = !tradeInExceeds;
        }

        if (!(additivePreviews instanceof HTMLElement) && !(salePricePreviewWrap instanceof HTMLElement)) {
            return;
        }

        if (!totals.hasAdditive) {
            if (additivePreviews instanceof HTMLElement) {
                additivePreviews.hidden = true;
            }
            if (salePricePreviewWrap instanceof HTMLElement) {
                salePricePreviewWrap.hidden = true;
            }
            return;
        }

        // PVC preview masqué si reprise > sous-total (jamais de montant négatif à l’écran).
        if (tradeInExceeds) {
            if (salePricePreviewWrap instanceof HTMLElement) {
                salePricePreviewWrap.hidden = true;
            }
        }

        // Ordre métier UI : véhicule+acc seulement si forfait (évite doublon montant avec PVC).
        const showVehiclePlus = hasAccessories
            && feeForTotals !== null
            && Math.abs(feeForTotals) >= 0.005;
        if (additivePreviews instanceof HTMLElement) {
            additivePreviews.hidden = !showVehiclePlus;
        }
        if (vehiclePlusPreview instanceof HTMLElement) {
            vehiclePlusPreview.hidden = !showVehiclePlus;
        }
        if (salePricePreviewWrap instanceof HTMLElement && !tradeInExceeds) {
            salePricePreviewWrap.hidden = false;
        }
        if (vehiclePlusValue instanceof HTMLElement) {
            vehiclePlusValue.textContent = `${formatSwissGroupedAmount(totals.vehiclePlus)} ${currencySuffix}`;
        }
        if (salePriceValue instanceof HTMLElement) {
            salePriceValue.textContent = `${formatSwissGroupedAmount(totals.salePrice)} ${currencySuffix}`;
        }
    };

    const updateNetPreview = () => {
        const discountType = discountTypeSelect.value;
        const catalogBasePrice = parseAmount(catalogBasePriceInput.value);
        const discountValue = parseAmount(discountValueInput.value);
        const netPrice = computeOfferNetPrice(catalogBasePrice, discountType, discountValue);

        if (netPrice === null) {
            netPreview.hidden = true;
            netPriceValue.textContent = '—';
            updateAdditivePreviews(null);
            return;
        }

        netPreview.hidden = false;
        netPriceValue.textContent = `${formatSwissGroupedAmount(netPrice)} ${currencySuffix}`;
        updateAdditivePreviews(netPrice);
    };

    // Affichage groupé de la remise uniquement pour montant / prix fixe.
    const formatDiscountValueDisplay = () => {
        if (discountValueInput.value.trim() === '' || !isMonetaryDiscountType(discountTypeSelect.value)) {
            return;
        }
        const rawForParse = discountValueInput.value.trim().replace(/[.,]$/, '');
        const parsed = parseSwissMoney(rawForParse);
        if (parsed !== null) {
            discountValueInput.value = formatSwissMoney(parsed, true);
        }
    };

    const formatDeliveryFeeDisplay = () => {
        if (!(deliveryFeeInput instanceof HTMLInputElement) || deliveryFeeInput.value.trim() === '') {
            return;
        }
        const rawForParse = deliveryFeeInput.value.trim().replace(/[.,]$/, '');
        const parsed = parseSwissMoney(rawForParse);
        if (parsed !== null) {
            deliveryFeeInput.value = formatSwissMoney(parsed, true);
        }
    };

    const formatTradeInAmountDisplay = () => {
        if (!(tradeInAmountInput instanceof HTMLInputElement) || tradeInAmountInput.value.trim() === '') {
            return;
        }
        const rawForParse = tradeInAmountInput.value.trim().replace(/[.,]$/, '');
        const parsed = parseSwissMoney(rawForParse);
        if (parsed !== null) {
            tradeInAmountInput.value = formatSwissMoney(parsed, true);
        }
    };

    // Forme canonique pour soumission uniquement.
    const toCanonicalValue = (input) => {
        if (input.value.trim() === '') {
            return;
        }
        const parsed = parseSwissMoney(input.value);
        if (parsed !== null) {
            input.value = String(parsed);
        }
    };

    const handleDiscountTypeChange = () => {
        const previousType = discountTypeSelect.dataset.previousType || discountTypeSelect.value;
        const nextType = discountTypeSelect.value;

        // Toujours via le helper : vide si type change, conserve sinon.
        discountValueInput.value = discountValueAfterTypeChange(
            previousType,
            nextType,
            discountValueInput.value,
        );

        discountTypeSelect.dataset.previousType = nextType;
        if (isMonetaryDiscountType(nextType)) {
            formatDiscountValueDisplay();
        } else {
            // Passage en pourcentage : forme canonique non groupée.
            toCanonicalValue(discountValueInput);
        }
        updateDiscountValueLabel();
        updateNetPreview();
    };

    const bindMoneyComposition = (input) => {
        input.addEventListener('compositionstart', () => {
            input.dataset.swissOfferComposing = '1';
        });
        input.addEventListener('compositionend', () => {
            input.dataset.swissOfferComposing = '0';
            applyMoneyLiveFormat(input);
            updateNetPreview();
        });
    };

    const bindAccessoryRow = (row) => {
        if (!(row instanceof HTMLElement) || row.dataset.offerAccessoryBound === '1') {
            return;
        }
        row.dataset.offerAccessoryBound = '1';

        const priceInput = row.querySelector('[data-offer-accessory-price]');
        const descInput = row.querySelector('input[name*="[description]"]');
        const removeButton = row.querySelector('[data-offer-accessory-remove]');

        if (priceInput instanceof HTMLInputElement) {
            bindMoneyComposition(priceInput);
            priceInput.addEventListener('input', (event) => {
                applyMoneyLiveFormat(priceInput, event);
                updateNetPreview();
            });
            priceInput.addEventListener('blur', () => {
                if (priceInput.value.trim() === '') {
                    updateNetPreview();
                    return;
                }
                const rawForParse = priceInput.value.trim().replace(/[.,]$/, '');
                const parsed = parseSwissMoney(rawForParse);
                if (parsed !== null) {
                    priceInput.value = formatSwissMoney(parsed, true);
                }
                updateNetPreview();
            });
        }

        if (descInput instanceof HTMLInputElement) {
            descInput.addEventListener('input', () => {
                updateNetPreview();
            });
        }

        if (removeButton instanceof HTMLButtonElement) {
            removeButton.addEventListener('click', () => {
                const visibleRows = getVisibleAccessoryRows();
                if (visibleRows.length <= 1) {
                    clearAccessoryRow(row);
                    updateNetPreview();
                    return;
                }
                clearAccessoryRow(row);
                row.hidden = true;
                updateAddButtonState();
                updateNetPreview();
            });
        }
    };

    getAccessoryRows().forEach(bindAccessoryRow);

    if (accessoryAddButton instanceof HTMLButtonElement && accessoriesList instanceof HTMLElement) {
        accessoryAddButton.addEventListener('click', () => {
            const nextHidden = getAccessoryRows().find((row) => row.hidden);
            if (!(nextHidden instanceof HTMLElement)) {
                updateAddButtonState();
                return;
            }
            nextHidden.hidden = false;
            const descInput = nextHidden.querySelector('input[name*="[description]"]');
            if (descInput instanceof HTMLInputElement) {
                descInput.focus();
            }
            updateAddButtonState();
            updateNetPreview();
        });
    }

    bindMoneyComposition(discountValueInput);
    if (deliveryFeeInput instanceof HTMLInputElement) {
        bindMoneyComposition(deliveryFeeInput);
        deliveryFeeInput.addEventListener('input', (event) => {
            applyMoneyLiveFormat(deliveryFeeInput, event);
            updateNetPreview();
        });
        deliveryFeeInput.addEventListener('blur', formatDeliveryFeeDisplay);
    }

    if (tradeInAmountInput instanceof HTMLInputElement) {
        bindMoneyComposition(tradeInAmountInput);
        tradeInAmountInput.addEventListener('input', (event) => {
            applyMoneyLiveFormat(tradeInAmountInput, event);
            updateNetPreview();
        });
        tradeInAmountInput.addEventListener('blur', formatTradeInAmountDisplay);
    }

    if (tradeInLabelInput instanceof HTMLInputElement) {
        tradeInLabelInput.addEventListener('input', () => {
            updateNetPreview();
        });
    }

    discountTypeSelect.addEventListener('change', handleDiscountTypeChange);

    discountValueInput.addEventListener('input', (event) => {
        if (isMonetaryDiscountType(discountTypeSelect.value)) {
            applyMoneyLiveFormat(discountValueInput, event);
        }
        updateNetPreview();
    });

    discountValueInput.addEventListener('blur', formatDiscountValueDisplay);

    const pricingForm = catalogBasePriceInput.form;
    if (pricingForm instanceof HTMLFormElement && pricingForm.dataset.offerPricingSubmitBound !== '1') {
        pricingForm.dataset.offerPricingSubmitBound = '1';
        pricingForm.addEventListener('submit', (event) => {
            toCanonicalValue(catalogBasePriceInput);
            if (isMonetaryDiscountType(discountTypeSelect.value)) {
                toCanonicalValue(discountValueInput);
            }
            if (deliveryFeeInput instanceof HTMLInputElement) {
                toCanonicalValue(deliveryFeeInput);
            }
            if (tradeInAmountInput instanceof HTMLInputElement) {
                toCanonicalValue(tradeInAmountInput);
            }
            getAccessoryRows().forEach((row) => {
                const priceInput = row.querySelector('[data-offer-accessory-price]');
                if (priceInput instanceof HTMLInputElement) {
                    toCanonicalValue(priceInput);
                }
            });

            const catalogBasePrice = parseAmount(catalogBasePriceInput.value);
            const discountValue = parseAmount(discountValueInput.value);
            const netPrice = computeOfferNetPrice(
                catalogBasePrice,
                discountTypeSelect.value,
                discountValue,
            );
            updateAdditivePreviews(netPrice);
            if (tradeInError instanceof HTMLElement && !tradeInError.hidden) {
                event.preventDefault();
            }

            window.setTimeout(() => {
                formatDiscountValueDisplay();
                formatDeliveryFeeDisplay();
                formatTradeInAmountDisplay();
            }, 0);
        });
    }

    discountTypeSelect.dataset.previousType = discountTypeSelect.value;
    updateDiscountValueLabel();
    formatDiscountValueDisplay();
    formatDeliveryFeeDisplay();
    formatTradeInAmountDisplay();
    updateAddButtonState();
    updateNetPreview();
}

export function init(documentRoot = document) {
    const roots = documentRoot.querySelectorAll('[data-offer-pricing-root]');
    roots.forEach((root) => {
        if (root instanceof HTMLElement) {
            initPartnerOfferPricing(root);
        }
    });
}

export default { init, computeOfferNetPrice, discountValueAfterTypeChange, sumAccessoryPricesFromInputs, computeAdditiveTotals };
